import { IconArrowsArrowDownR } from '@doltech/icons/IconArrowsArrowDownR';
import { IconArrowsRefresh } from '@doltech/icons/IconArrowsRefresh';
import { IconButtonsPlayPauseO } from '@doltech/icons/IconButtonsPlayPauseO';
import { IconMultimediaPlayButtonO } from '@doltech/icons/IconMultimediaPlayButtonO';
import { IconOtherQuestionnaire1 } from '@doltech/icons/IconOtherQuestionnaire1';
import { IconOtherTranscript } from '@doltech/icons/IconOtherTranscript';
import { IconOtherVocab } from '@doltech/icons/IconOtherVocab';
import { components } from '@doltech/pageManagement/dto';
import {
  PageTypeEnum,
  SkillEnum,
  TestStatusEnum,
  TestTypeEnum,
} from '@doltech/utils/lib/constants';
import { imgProxyLoader } from '@doltech/utils/lib/image-loader';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import * as React from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';

import {
  deviceBreakpoints,
  SHARED_TABLET_CONTENT_WIDTH,
} from '../../../../hooks/useDeviceQuery.hook';
import { dolDinhLucAuthor } from '../../../Blog/dolDinhLucAuthor.data';
import { BreadcrumbDolVn } from '../../../Breadcrumb/BreadcrumbDolVn';
import { colorsV2 } from '../../../colors-v2';
import { AspectRatio } from '../../../Common/AspectRatio';
import { Box } from '../../../Common/BoxV2';
import { DOLTypographyVariant, Typography } from '../../../DesignSystemV2/Typography';
import { Link } from '../../../Link';
import renderNode, { Div } from '../../../renderNode';
import { fetchUserTests } from '../api/user-test-landing';
import { Separator } from '../Content/Separator';
import { useLandingSharedDataStore } from '../model/landing-shared-data.hook';
import { AuthorInfo } from './AuthorInfo';
import withHydrationOnDemand from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import { ResponsiveTypography } from '../../../Typography/v3/ResponsiveTypography';
import { fixSafariDate } from '@doltech/utils/lib/date';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/no-unused-prop-types */
const Main = styled.div.withConfig({
  componentId: 'HeaderSEO_Main',
} as any)`
  max-width: ${SHARED_TABLET_CONTENT_WIDTH}px;
  margin: 0 auto;
  padding: 28px 16px;

  .extra-container {
    display: grid;
    grid-auto-flow: column;
    border-top: 1px solid ${colorsV2.black10};
    border-bottom: 1px solid ${colorsV2.black10};
    align-items: center;
    gap: 8px;
    padding: 16px 0;
  }

  @media ${deviceBreakpoints.fromTablet} {
    padding: 32px 0 0 0;
  }

  @media ${deviceBreakpoints.mobile} {
    padding: 28px 16px 0 16px;
  }
`;

interface LandingHeaderSEOProps {
  seoData: components['schemas']['PageResponseDTOObject'];
  seoPageData: components['schemas']['SEOPageDTO'][];
  testId?: string;
  testType?: TestTypeEnum;
  testSkill?: SkillEnum;
  children?: React.FC | JSX.Element | JSX.Element[];
}

const defaultHeader = {
  title: '...',
  description: '...',
  fileInfo: { url: 'https://dummyimage.com/100x100.png' },
  type: 'VIEW_TEST_QUESTION',
};

const ellipsis = {
  rows: 3,
  expandable: true,
  symbol: (
    <ResponsiveTypography.Text className="font-noto" variant="regular/20-32">
      See more
    </ResponsiveTypography.Text>
  ),
};

const ActionLinkMain = styled.div.withConfig({
  componentId: 'HeaderSEO_ActionLinkMain',
} as any)`
  .action {
    display: flex;
    align-items: center;
    white-space: pre-wrap;
    font-size: 24px;
    padding-right: 24px;
    color: ${colorsV2.black100};

    @media screen and ${deviceBreakpoints.mobile} {
      flex-direction: column;
      padding: 4px;

      &:not(:last-child) {
        margin-right: 16px;
      }
    }

    &:hover {
      span {
        text-decoration: underline;
      }
    }

    &-icon {
      font-size: 24px;
      margin-right: 8px;

      @media screen and ${deviceBreakpoints.mobile} {
        margin-right: 0;
        margin-bottom: 4px;
      }
    }

    &-view-explanation {
      .action-icon {
        color: ${colorsV2.green100};
      }
    }

    &-view-vocab {
      .action-icon {
        color: ${colorsV2.yellow100};
      }
    }

    &-view-transcript {
      .action-icon {
        color: ${colorsV2.purple100};
      }
    }

    &-download-test {
      .action-icon {
        color: ${colorsV2.blue100};
      }
    }
  }

  &.action-links {
    display: flex;
    align-items: center;
    pointer-events: initial;

    @media screen and ${deviceBreakpoints.mobile} {
      justify-content: space-between;
      padding: 0;
    }

    button {
      background-color: ${colorsV2.white100};
      font-size: 12px;
      line-height: 16px;
      color: ${colorsV2.black100};
      font-weight: 500;
    }
  }
`;

interface ActionLinksProps extends LandingHeaderSEOProps {
  textSizeVariant?: DOLTypographyVariant;
}

const ActionLinks = observer((props: ActionLinksProps) => {
  const { seoPageData = [], seoData, textSizeVariant = 'medium/14', testType } = props;
  const { isLoggedIn } = useGetAuthInfo();
  const actionDoTest = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.DO_TEST &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.DO_TEST),
    [seoPageData, seoData.type]
  );

  const actionViewTestQuestion = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.VIEW_TEST_QUESTION &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.VIEW_TEST_QUESTION),
    [seoPageData, seoData.type]
  );

  const actionViewVocab = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.VIEW_VOCAB &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.VIEW_VOCAB),
    [seoPageData, seoData.type]
  );
  const actionViewSolution = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.VIEW_SOLUTION &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.VIEW_SOLUTION),
    [seoPageData, seoData.type]
  );
  const actionViewTranscript = React.useMemo(
    () =>
      seoData.type !== PageTypeEnum.VIEW_TRANSCRIPT &&
      seoPageData.find((item) => item.templateTypeId === PageTypeEnum.VIEW_TRANSCRIPT),
    [seoPageData, seoData.type]
  );

  const renderActionText = React.useCallback(
    (children) => {
      const variant: DOLTypographyVariant = textSizeVariant;
      return (
        <Typography.Text variant="medium/12-20" tabletVariant={variant}>
          {children}
        </Typography.Text>
      );
    },
    [textSizeVariant]
  );

  const { data: overviews } = useQuery(
    'FETCH_USER_TEST_INPROGRESS',
    () =>
      fetchUserTests(
        testType,
        testType === TestTypeEnum.FULL_TEST ? [seoData.data?.id] : null,
        testType === TestTypeEnum.PRACTICE_TEST ? [seoData.data?.id] : null
      ),
    {
      enabled: isLoggedIn,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchInterval: false,
      refetchOnMount: true,
      retry: false,
    }
  );

  const renderDoTestButton = React.useCallback(() => {
    let buttonIcon;
    let buttonText;

    switch (overviews?.content?.[0]?.status) {
      case TestStatusEnum.IN_PROGRESS:
        buttonIcon = <IconButtonsPlayPauseO className="action-icon" color={colorsV2.yellow100} />;
        buttonText = 'Làm Tiếp';
        break;
      case TestStatusEnum.COMPLETED:
        buttonIcon = <IconArrowsRefresh className="action-icon" color={colorsV2.green100} />;
        buttonText = 'Làm Lại';
        break;
      default:
        buttonIcon = (
          <IconMultimediaPlayButtonO className="action-icon" color={colorsV2.primary100} />
        );
        buttonText = 'Làm Bài';
        break;
    }

    return (
      <>
        {buttonIcon}
        {renderActionText(buttonText)}
      </>
    );
  }, [overviews?.content, renderActionText]);

  return (
    <ActionLinkMain className="action-links">
      {actionDoTest?.url && (
        <Link
          target="_blank"
          className="action action-do-test"
          href={getLandingUrlFromPath(actionDoTest.url)}
          prefetch={false}
        >
          {renderDoTestButton()}
        </Link>
      )}
      {actionViewTestQuestion?.url && (
        <Link
          className="action action-download-test"
          href={getLandingUrlFromPath(actionViewTestQuestion.url)}
          target="_blank"
          prefetch={false}
        >
          <IconArrowsArrowDownR className="action-icon" />
          {renderActionText('Đề và đáp án')}
        </Link>
      )}
      {actionViewSolution?.url && (
        <Link
          className="action action-view-explanation"
          href={getLandingUrlFromPath(actionViewSolution.url)}
          target="_blank"
          prefetch={false}
        >
          <IconOtherQuestionnaire1 className="action-icon" />
          {renderActionText('Xem giải thích')}
        </Link>
      )}
      {actionViewVocab?.url && (
        <Link
          className="action action-view-vocab"
          href={getLandingUrlFromPath(actionViewVocab.url)}
          target="_blank"
          prefetch={false}
        >
          <IconOtherVocab className="action-icon" />
          {renderActionText('Từ vựng')}
        </Link>
      )}
      {actionViewTranscript?.url && (
        <Link
          className="action action-view-transcript"
          href={getLandingUrlFromPath(actionViewTranscript.url)}
          target="_blank"
          prefetch={false}
        >
          <IconOtherTranscript className="action-icon" />
          {renderActionText('Transcript')}
        </Link>
      )}
    </ActionLinkMain>
  );
});

const HeaderSEOBGMain = styled.div`
  &&& {
    .title-text {
      font-weight: 400;
    }
  }
`;

const LandingHeaderSEOLayout = (props: LandingHeaderSEOProps) => {
  const { seoData, children, testSkill, testType } = props;
  const { description, fileInfo, title } = seoData.dol || defaultHeader;
  const store = useLandingSharedDataStore();

  const breadcrumbTopLevel = React.useMemo(() => {
    if (testType === TestTypeEnum.FULL_TEST) {
      return [
        {
          breadcrumbName: 'IELTS ONLINE TEST',
          path: store.pageLink?.staticPageLink?.LANDING_FULL_TEST?.url || '#',
        },
      ];
    }
    if (testType === TestTypeEnum.PRACTICE_TEST) {
      switch (testSkill) {
        case SkillEnum.LISTENING: {
          return [
            {
              breadcrumbName: 'IELTS ONLINE TEST',
              path: store.pageLink?.staticPageLink?.LANDING_FULL_TEST?.url || '#',
            },
            {
              breadcrumbName: 'IELTS LISTENING PRACTICE TESTS',
              path: store.pageLink?.staticPageLink?.LANDING_LISTENING_PRACTICE_TEST?.url || '#',
            },
          ];
        }
        case SkillEnum.READING: {
          return [
            {
              breadcrumbName: 'IELTS ONLINE TEST',
              path: store.pageLink?.staticPageLink?.LANDING_FULL_TEST?.url || '#',
            },
            {
              breadcrumbName: 'IELTS READING PRACTICE TESTS',
              path: store.pageLink?.staticPageLink?.LANDING_READING_PRACTICE_TEST?.url || '#',
            },
          ];
        }
        default:
      }
    }
    if (seoData.type === PageTypeEnum.DO_DICTATION) {
      return [
        {
          breadcrumbName: store?.pageLink?.staticPageLink?.LANDING_DICTATION?.dol?.title || '...',
          path: store?.pageLink?.staticPageLink?.LANDING_DICTATION?.url || '#',
        },
      ];
    }

    return [
      {
        breadcrumbName: '...',
        path: '#',
      },
    ];
  }, [
    seoData.type,
    store?.pageLink?.staticPageLink?.LANDING_DICTATION?.dol?.title,
    store?.pageLink?.staticPageLink?.LANDING_DICTATION?.url,
    store?.pageLink?.staticPageLink?.LANDING_FULL_TEST?.url,
    store?.pageLink?.staticPageLink?.LANDING_LISTENING_PRACTICE_TEST?.url,
    store?.pageLink?.staticPageLink?.LANDING_READING_PRACTICE_TEST?.url,
    testSkill,
    testType,
  ]);

  return (
    <HeaderSEOBGMain className="header-seo-bg">
      <div className="header-seo-content">
        <Box mb="20px" className="header-row breadcrumb-container">
          <BreadcrumbDolVn
            routes={[
              {
                breadcrumbName: 'Trang chủ',
                path: store?.pageLink?.staticPageLink?.LANDING_HOME?.url || '/',
              },
              ...breadcrumbTopLevel,
            ]}
          />
        </Box>
        <div className="header-row seo-test-header">
          <div className="info-container">
            <ResponsiveTypography.Title
              mb={['8px', '16px']}
              level={1}
              variant={['regular/32-48', 'regular/42-60', 'regular/42-60']}
              className="title-text font-noto"
            >
              {title}
            </ResponsiveTypography.Title>

            <Typography.Paragraph
              className="desc-text font-noto"
              variant="regular/20-32"
              ellipsis={ellipsis}
              color="black70"
            >
              {description}
            </Typography.Paragraph>
            <AuthorInfo
              pageId={seoData.id}
              author={dolDinhLucAuthor}
              publishedDate={fixSafariDate(seoData.lastModifiedAt)}
              seoUrl={seoData.url}
            />
            <Box mb={['16px', '32px']} className="extra-container">
              {renderNode(Div, children, props)}
            </Box>

            <Box mb="24px" className="img-cover" bg="gray20">
              <AspectRatio ratio={3 / 2}>
                <Image
                  layout="fill"
                  objectFit="contain"
                  src={fileInfo?.url}
                  alt={title}
                  priority
                  loader={imgProxyLoader}
                />
              </AspectRatio>
            </Box>

            <Separator pb="40px" variant="three-dot-horizontal" />
          </div>
        </div>
      </div>
    </HeaderSEOBGMain>
  );
};

export const LandingHeaderSEO = withHydrationOnDemand({ on: ['idle'] })(
  (props: LandingHeaderSEOProps) => {
    return (
      <Main className="header-seo-container">
        <LandingHeaderSEOLayout {...props}>
          <ActionLinks {...props} />
        </LandingHeaderSEOLayout>
      </Main>
    );
  }
);
